import { useState } from 'react'
import { RETURN_REASONS } from '../hooks/useCustomerFlags'

const BLUE  = '#1877F2'
const TEXT  = '#050505'
const MUTED = '#65676B'
const BORDER = '#E4E6EB'
const BG    = '#F0F2F5'
const WHITE = '#FFFFFF'
const AMBER = '#D97706'

export default function ReturnReasonModal({ orderCode, onConfirm, onClose }) {
  const [side, setSide]     = useState('') // 'seller' | 'customer'
  const [reason, setReason] = useState('')
  const [detail, setDetail] = useState('')
  const [saving, setSaving] = useState(false)

  const reasons = side === 'seller' ? RETURN_REASONS.seller : side === 'customer' ? RETURN_REASONS.customer : []

  const handleConfirm = async () => {
    if (!side || !reason) return
    setSaving(true)
    await onConfirm({
      returnReasonCategory: reason,
      returnReasonDetail: detail,
      isSellerFault: side === 'seller',
    })
    setSaving(false)
    onClose()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '16px' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: WHITE, borderRadius: '16px', width: '100%', maxWidth: '480px', fontFamily: 'Space Grotesk, system-ui, sans-serif', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BORDER, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: WHITE, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>Mark as Return</div>
            <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>{orderCode}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: MUTED }}>×</button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ fontSize: '12px', color: MUTED, marginBottom: '16px', lineHeight: 1.6 }}>
            Please select the return reason honestly. Customer-side returns may contribute to flagging patterns, which helps protect all sellers on PageConnect.
          </div>

          {/* Who is at fault */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: MUTED, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Who is responsible for this return?</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button onClick={() => { setSide('seller'); setReason('') }}
                style={{ padding: '14px', borderRadius: '10px', border: `2px solid ${side === 'seller' ? BLUE : BORDER}`, background: side === 'seller' ? '#EFF6FF' : WHITE, cursor: 'pointer', textAlign: 'center', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                <div style={{ fontSize: '20px', marginBottom: '6px' }}>🏪</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: side === 'seller' ? BLUE : TEXT }}>Our side</div>
                <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>Wrong item, defective, size error</div>
              </button>
              <button onClick={() => { setSide('customer'); setReason('') }}
                style={{ padding: '14px', borderRadius: '10px', border: `2px solid ${side === 'customer' ? AMBER : BORDER}`, background: side === 'customer' ? '#FFFBEB' : WHITE, cursor: 'pointer', textAlign: 'center', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                <div style={{ fontSize: '20px', marginBottom: '6px' }}>👤</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: side === 'customer' ? AMBER : TEXT }}>Customer side</div>
                <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>Changed mind, refused, no reason</div>
              </button>
            </div>
          </div>

          {/* Specific reason */}
          {side && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: MUTED, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Specific reason *</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {reasons.map(r => (
                  <button key={r.key} onClick={() => setReason(r.key)}
                    style={{ padding: '10px 14px', borderRadius: '9px', border: `2px solid ${reason === r.key ? (side === 'seller' ? BLUE : AMBER) : BORDER}`, background: reason === r.key ? (side === 'seller' ? '#EFF6FF' : '#FFFBEB') : WHITE, cursor: 'pointer', textAlign: 'left', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: reason === r.key ? (side === 'seller' ? BLUE : AMBER) : TEXT }}>{r.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Customer-side warning */}
          {side === 'customer' && reason && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '12px', color: '#92400E', lineHeight: 1.6 }}>
              This return will be recorded as customer-initiated. If this customer has multiple customer-side returns with you, they may be privately flagged on your account. Community flags only appear after multiple sellers report the same number.
            </div>
          )}

          {/* Extra detail */}
          {side && reason && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: MUTED, marginBottom: '5px' }}>Additional notes <span style={{ fontWeight: 400, color: '#B0B3B8' }}>(optional)</span></div>
              <textarea value={detail} onChange={e => setDetail(e.target.value)}
                placeholder="Any extra details about this return..."
                rows={2}
                style={{ width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 12px', fontSize: '12.5px', outline: 'none', resize: 'none', fontFamily: 'Space Grotesk, system-ui, sans-serif', color: TEXT, background: BG, boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = BLUE}
                onBlur={e => e.target.style.borderColor = BORDER} />
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose}
              style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={!side || !reason || saving}
              style={{ flex: 1, padding: '11px', borderRadius: '10px', background: side && reason && !saving ? (side === 'seller' ? BLUE : AMBER) : BORDER, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: side && reason && !saving ? 'pointer' : 'not-allowed', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
              {saving ? 'Saving...' : 'Confirm Return'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}